import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import WarningModal from "../modal/index";
import {
  checkItemAvailabilityAsync,
  getPickupBranchDetails,
} from "../../redux/features/restaurants/restaurantThunks";
import {
  selectCartItems,
  selectCartRestaurantDetails,
  selectUnavailableItems,
} from "../../redux/features/restaurants/restaurantSelectors";
import {
  selectActiveOrders,
  selectServiceType,
} from "../../redux/features/customer/customerSelectors";

const AvailableBranches = ({
  renderingType = "type1",
  branches = [],
  onBranchSelect,
  selectedBranchId,
  selectedBranchName,
  selectedBranchArea,
  isCheckout = false,
}) => {
  // Initialize activeButton with selectedBranchId if available
  const [activeButton, setActiveButton] = useState(selectedBranchId || null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(
    selectedBranchId
      ? {
          id: selectedBranchId,
          name: selectedBranchName,
          area: selectedBranchArea,
        }
      : null
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cartItems = useSelector(selectCartItems);
  const cartRestaurantDetails = useSelector(selectCartRestaurantDetails);
  const unavailableItems = useSelector(selectUnavailableItems);
  const activeOrders = useSelector(selectActiveOrders);
  const serviceType = useSelector(selectServiceType);

  // Get current service type (pickup or delivery)
  const currentServiceType = serviceType || "delivery";

  // Check if a branch has an active order with matching service type
  const hasActiveOrder = (branchId, restaurantId) => {
    return activeOrders.some(
      (order) =>
        (order.branch_id === branchId ||
          order.restaurant_id === restaurantId) &&
        order.service_type === currentServiceType
    );
  };

  // Use branches from props if available, otherwise use the default buttons
  const branchButtons =
    branches && branches.length > 0
      ? branches.map((branch) => ({
          id: branch.id,
          name: branch.name_en || branch.name || "Branch",
          area: branch.area || "",
          restaurant_id: branch.restaurant_id,
        }))
      : [
          { id: 1, name: "Bidaa" },
          { id: 2, name: "Avenues" },
          { id: 3, name: "Al Kout" },
          { id: 4, name: "Khiran" },
          { id: 5, name: "Jahra" },
          { id: 6, name: "Al Hamra" },
        ];
  // Update activeButton and selectedBranch when props change
  useEffect(() => {
    if (selectedBranchId) {
      setActiveButton(selectedBranchId);

      // Only update selectedBranch if it doesn't exist or has a different ID
      if (!selectedBranch || selectedBranch.id !== selectedBranchId) {
        setSelectedBranch({
          id: selectedBranchId,
          name:
            selectedBranchName ||
            branchButtons.find((b) => b.id === selectedBranchId)?.name ||
            "",
          area:
            selectedBranchArea ||
            branchButtons.find((b) => b.id === selectedBranchId)?.area ||
            "",
        });
      }

      // If we have a selectedBranchId but no onBranchSelect has been called yet,
      // call it to ensure the parent component is updated
      if (
        onBranchSelect &&
        (!selectedBranch || selectedBranch.id !== selectedBranchId)
      ) {
        const branchName =
          selectedBranchName ||
          branchButtons.find((b) => b.id === selectedBranchId)?.name ||
          "";
        const branchArea =
          selectedBranchArea ||
          branchButtons.find((b) => b.id === selectedBranchId)?.area ||
          "";
        onBranchSelect(selectedBranchId, branchName, branchArea);
      }
    }
  }, [selectedBranchId, selectedBranchName, selectedBranchArea, branchButtons]);

  // Ensure branch selection is applied on initial render
  useEffect(() => {
    if (selectedBranchId && activeButton !== selectedBranchId) {
      setActiveButton(selectedBranchId);
    }
  }, []);

  const handleClick = async (id, branchName, branchArea) => {
    setSelectedBranch({ id, name: branchName, area: branchArea });

    // Check if we're in checkout mode and have items in cart
    if ((isCheckout || cartItems.length > 0) && cartRestaurantDetails) {
      try {
        // Get all item IDs from the cart
        const itemIds = cartItems.map((item) => item.id);

        // Check item availability at new branch
        const response = await dispatch(
          checkItemAvailabilityAsync({
            restaurant_id: cartRestaurantDetails.restaurantId,
            branch_id: id,
            item_ids: itemIds,
          })
        );

        // Check if there are unavailable items
        const unavailableItemIds = response.payload.unavailable_item_ids || [];

        if (unavailableItemIds.length > 0) {
          // Show modal if there are unavailable items
          setModalOpen(true);
        } else {
          // If no items are unavailable, directly select the branch
          setActiveButton(id);
          if (onBranchSelect) {
            onBranchSelect(id, branchName, branchArea);
          }

          // If on checkout page, get branch details
          if (isCheckout) {
            await dispatch(getPickupBranchDetails(id));
          } else {
            // If not in checkout mode, fetch branch details and navigate to place-order
            await dispatch(getPickupBranchDetails(id));
            navigate("/place-order");
          }
        }
      } catch (error) {
        console.error("Error checking item availability:", error);
        // Select the branch anyway in case of error
        // setActiveButton(id);
        // if (onBranchSelect) {
        //   onBranchSelect(id, branchName, branchArea);
        // }

        // If on checkout page, get branch details even if availability check failed
        // if (isCheckout) {
        //   await dispatch(
        //     getBranchDetailsAsync(
        //       cartRestaurantDetails.restaurantId,
        //       branchArea,
        //       branchName
        //     )
        //   );
        // }
      }
    } else {
      // If not in checkout mode or no items in cart, select branch without checking
      setActiveButton(id);
      if (onBranchSelect) {
        onBranchSelect(id, branchName, branchArea);
      }

      // If on checkout page, get branch details
      if (isCheckout) {
        await dispatch(getPickupBranchDetails(id));
      } else {
        // If not in checkout mode, fetch branch details and navigate to place-order
        await dispatch(getPickupBranchDetails(id));
        navigate("/place-order");
      }
    }
  };

  const handleCancel = () => {
    setModalOpen(false);
    setSelectedBranch(null);
  };

  const handleProceed = async () => {
    setModalOpen(false);
    if (selectedBranch) {
      setActiveButton(selectedBranch.id);
      if (onBranchSelect) {
        onBranchSelect(
          selectedBranch.id,
          selectedBranch.name,
          selectedBranch.area
        );
      }

      // Fetch branch details and navigate to place-order (unless in checkout mode)
      if (!isCheckout) {
        await dispatch(getPickupBranchDetails(selectedBranch.id));
        navigate("/place-order");
      } else {
        await dispatch(getPickupBranchDetails(selectedBranch.id));
      }
    }
  };

  const getBranchName = () => {
    if (selectedBranch?.name) return selectedBranch.name;
    if (activeButton) {
      const button = branchButtons.find((btn) => btn.id === activeButton);
      return button?.name || "";
    }
    return "";
  };

  return (
    <>
      <h1 className="text-3xl font-bold text-gray-500">
        Available Branches for Pickup
      </h1>
      <div
        className="grid gap-6 w-full mt-5"
        style={{
          gridTemplateColumns: "repeat(auto-fill, minmax(257px, 1fr))",
        }}
      >
        {branchButtons.map((button) => (
          <div
            key={button.id}
            onClick={() => handleClick(button.id, button.name, button.area)}
            className={`relative flex items-center justify-center border-2 rounded-xl min-w-[257px] max-w-[257px] w-[257px] min-h-[56px] p-4 cursor-pointer
              ${
                activeButton === button.id
                  ? renderingType === "type1"
                    ? "bg-amber-100 text-white border-gray-70"
                    : "bg-gray-50 text-white border-slate-500"
                  : renderingType === "type2"
                  ? "bg-amber-100 text-white border-gray-700"
                  : "bg-white text-neutral-500 border-slate-500"
              }
            `}
          >
            <span className="text-base font-semibold text-center break-words">
              {button.name}
            </span>
            {hasActiveOrder(button.id, button.restaurant_id) && (
              <div className="absolute top-2 right-2">
                <span className="inline-block px-2 py-1 bg-emerald-600 text-white text-xs font-bold rounded-lg whitespace-nowrap">
                  Active Order
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Render the WarningModal only when there are unavailable items */}
      {modalOpen && unavailableItems && unavailableItems.length > 0 && (
        <WarningModal
          onCancel={handleCancel}
          onProceed={handleProceed}
          description={`You are about to change the selected branch to ${getBranchName()}!`}
          subDescription="Below items are not available on the new branch and will be removed from the cart."
          unavailableItems={unavailableItems}
        />
      )}
    </>
  );
};

export default AvailableBranches;
